import { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Text } from '@/components/ui';
import { chartAxisTick, chartColor } from '@/lib/design-system/color-system';
import { fetchAllTransactions, fetchCategories } from '@/lib/finance/api';
import { formatUsd } from '@/lib/finance/format';
import { addMonths, monthLabelShort, monthStart } from '@/lib/finance/month';
import {
  incomeExpenseTotals,
  monthlyIncomeExpense,
  type IncomeExpense,
  type MonthlyIncomeExpense,
} from '@/lib/finance/report';
import type { Transaction } from '@/lib/finance/types';

const TREND_MONTHS = 6;

function StatCard({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <View className="min-w-[140px] flex-1 rounded-lg border border-border px-4 py-3">
      <Text variant="caption" className="text-muted">
        {label}
      </Text>
      <Text variant="h3" className={tone}>
        ${formatUsd(value)}
      </Text>
    </View>
  );
}

export function IncomeVsExpense({ month }: { month: string }) {
  const [totals, setTotals] = useState<IncomeExpense | null>(null);
  const [series, setSeries] = useState<MonthlyIncomeExpense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const from = monthStart(addMonths(month, -(TREND_MONTHS - 1)));
        const to = monthStart(addMonths(month, 1));
        const [categories, transactions] = await Promise.all([
          fetchCategories(),
          fetchAllTransactions({ from, to }),
        ]);
        if (cancelled) return;
        const monthTx: Transaction[] = transactions.filter((t) => t.date.slice(0, 7) === month);
        setTotals(incomeExpenseTotals(categories, monthTx));
        setSeries(monthlyIncomeExpense(categories, transactions));
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to build report');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [month]);

  const chartData = useMemo(() => {
    const wanted: string[] = [];
    for (let i = TREND_MONTHS - 1; i >= 0; i -= 1) wanted.push(addMonths(month, -i));
    const byMonth = new Map(series.map((s) => [s.month, s]));
    return wanted.map((m) => ({
      name: monthLabelShort(m),
      Income: byMonth.get(m)?.income ?? 0,
      Expense: byMonth.get(m)?.expense ?? 0,
    }));
  }, [series, month]);

  if (loading) return <ActivityIndicator />;
  if (error)
    return (
      <Text variant="body-sm" className="text-danger">
        {error}
      </Text>
    );

  return (
    <View>
      <View className="flex-row flex-wrap gap-3">
        <StatCard label="Income" value={totals?.incomeUsd ?? 0} tone="text-success" />
        <StatCard label="Expense" value={totals?.expenseUsd ?? 0} tone="text-danger" />
        <StatCard
          label="Net"
          value={totals?.netUsd ?? 0}
          tone={(totals?.netUsd ?? 0) >= 0 ? 'text-success' : 'text-danger'}
        />
      </View>

      <View className="mt-6">
        <Text variant="h3" className="mb-2">
          Last {TREND_MONTHS} months
        </Text>
        <div style={{ width: '100%', height: 320 }}>
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
              <CartesianGrid stroke={chartColor.grid} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={chartAxisTick} />
              <YAxis tick={chartAxisTick} />
              <Tooltip formatter={(value: unknown) => `$${formatUsd(Number(value))}`} />
              <Legend wrapperStyle={{ color: chartColor.tick, fontSize: 12 }} />
              <Bar dataKey="Income" fill={chartColor.income} />
              <Bar dataKey="Expense" fill={chartColor.expense} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </View>

      <Text variant="caption" className="mt-3 text-muted">
        Transfers are excluded so net reflects real cash flow. Rows missing an FX rate for their
        month are omitted from totals.
      </Text>
    </View>
  );
}
